import { Menu, Transition } from "@headlessui/react";
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import {
  AdjustmentsVerticalIcon,
  CodeBracketSquareIcon,
  CubeTransparentIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { WorkflowEditTypes } from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { INPUT_OUTPUT_PARAMS, NODE_TYPES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { Fragment, type JSX, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import {
  filterBySearchTerm,
  filterNodesByConditions,
  processTree,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";

export default function Sidebar({
  options,
  onCreateProcess,
  onCreatePipeline,
  onImportProcessFromGit,
  onClickSidebarItem,
  showMenu,
}): JSX.Element {
  const [term, setTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState(options);
  const [isFilteringByText, setIsFilteringByText] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: boolean;
  }>({ public: false, private: false });
  const [openId, setOpenId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isGrabbing, setIsGrabbing] = useState(false);

  useEffect(() => {
    if (options) {
      const processed = processTree(options);
      setFilteredItems(processed);
    }
  }, [options]);

  useEffect(() => {
    const conditions = node => {
      if (selectedFilters.public && selectedFilters.private) {
        return true;
      }
      if (selectedFilters.public) {
        return node.isPublic === true;
      }
      if (selectedFilters.private) {
        return node.isPublic === false;
      }
      return true;
    };

    let filteredNodes = filterNodesByConditions(options, conditions);
    if (term) {
      filteredNodes = filterBySearchTerm(filteredNodes, term);
    }
    setFilteredItems(filteredNodes);
  }, [selectedFilters, term]);

  const handleMouseDown = () => {
    setIsGrabbing(true);
  };

  const handleMouseUp = () => {
    setIsGrabbing(false);
  };

  const toggleOpen = (id, category): void => {
    if (isFilteringByText) {
      return;
    }
    setSelectedCategory(
      selectedCategory === category && openId === id ? null : category
    );
    setOpenId(openId === id ? null : id);
  };

  const onDragStart = (event, nodeType, id?): void => {
    event.dataTransfer.setData("dropData", `${nodeType}-${id}`);
    event.dataTransfer.effectAllowed = "move";
  };

  const filterData = (value): void => {
    const filtered =
      value === "" ? options : filterBySearchTerm(options, value);
    setIsFilteringByText(value !== "");
    setFilteredItems(filtered);
    setTerm(value);
  };

  const handleFilterConditionChange = (e): void => {
    const { name, checked } = e.target;
    setSelectedFilters(prev => ({ ...prev, [name]: checked }));
    e.stopPropagation();
  };

  return (
    <aside className="max-h-[78dvh] overflow-scroll py-2">
      {showMenu && (
        <span className="text-xs">
          <button
            id="createPipeline"
            type="button"
            title="Create Pipeline"
            onClick={onCreatePipeline}
            className="folder-actions-button rounded-l-md text-gray-500">
            <CubeTransparentIcon className="folder-actions-icon" />
          </button>
          <Tooltip
            anchorSelect={"#createPipeline"}
            content={"Create Pipeline"}
            place="top-start"
          />
          <button
            id="createProcess"
            type="button"
            title="Create Process"
            onClick={onCreateProcess}
            className="folder-actions-button -ml-px text-gray-500">
            <ViewfinderCircleIcon className="folder-actions-icon" />
          </button>
          <Tooltip
            anchorSelect={"#createProcess"}
            content={"Create Process"}
            place="top"
          />
          <button
            id="importProcessFromGit"
            type="button"
            title="Import Process from Git"
            onClick={onImportProcessFromGit}
            className="folder-actions-button -ml-px text-gray-500">
            <CodeBracketSquareIcon className="folder-actions-icon" />
          </button>
          <Tooltip
            anchorSelect={"#importProcessFromGit"}
            content={"Import Process from Git"}
            place="top"
          />
          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button
              id="filter"
              type="button"
              title="Filter"
              className="folder-actions-button -ml-px rounded-r-md text-gray-500">
              <AdjustmentsVerticalIcon className="folder-actions-icon" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95">
              <Menu.Items className="absolute right-0 z-20 mt-1 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    <div className="flex flex-col px-3 py-1">
                      <div
                        onClick={e => e.stopPropagation()}
                        className="flex flex-row pb-3">
                        <input
                          name="public"
                          checked={selectedFilters.public}
                          onChange={handleFilterConditionChange}
                          type="checkbox"
                          className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        Public
                      </div>
                      <div
                        onClick={e => e.stopPropagation()}
                        className="flex flex-row">
                        <input
                          name="private"
                          checked={selectedFilters.private}
                          onChange={handleFilterConditionChange}
                          type="checkbox"
                          className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        Private
                      </div>
                    </div>
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
          <Tooltip
            anchorSelect={"#filter"}
            content={"Filter"}
            place="top-end"
          />
        </span>
      )}
      <div className="relative my-2 rounded-md shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon
            title="Search Files / Folders"
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
        <input
          type="search"
          placeholder="Search..."
          id="search"
          value={term}
          onChange={e => {
            filterData(e.target.value);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
      </div>
      <div className="mb-4">
        <span className="text-lg font-semibold">Inputs / Outputs</span>
        {INPUT_OUTPUT_PARAMS.map((io, i) => (
          <dd
            key={`${i}-${io.name}`}
            className={classNames(
              isGrabbing ? "cursor-grabbing" : "cursor-grab",
              "my-2 flex flex-row items-center rounded-md bg-gray-50 pl-2 text-gray-700 duration-200 ease-in-out hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm"
            )}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} // In case the mouse leaves the tile while dragging
            onDragStart={event => {
              onDragStart(event, io.nodeType);
            }}
            draggable>
            <span className={`text-[10px] leading-none ${io.color}`}>
              {"\u2B24"}
            </span>
            <h3 className="py-2 text-xs">&nbsp;{io.name}</h3>
          </dd>
        ))}
      </div>
      <dl>
        {filteredItems?.map(item => (
          <div key={item.id} className="mb-4">
            <span className="text-lg font-semibold">{item.name}</span>
            {item?.children.map((child, idx) => (
              <Fragment key={`${item.id}-${child.id}-${idx}`}>
                <dt
                  className={classNames(
                    selectedCategory === child.name &&
                      idx === openId &&
                      !isFilteringByText
                      ? "border-indigo-500 font-medium text-indigo-600"
                      : "",
                    "flex cursor-pointer flex-row justify-between pt-2 font-normal transition-all duration-200"
                  )}
                  onClick={() => {
                    toggleOpen(idx, child.name);
                  }}>
                  <span className="group inline-flex items-center">
                    <span className="hover:text-indigo-600">{child.name}</span>
                    <span className="ml-2 mt-0.5 text-xs opacity-0 duration-100 ease-in-out group-hover:text-indigo-600 group-hover:opacity-100">
                      {child.children.length}
                    </span>
                  </span>
                  <ChevronRightIcon
                    className={classNames(
                      selectedCategory === child.name && idx === openId
                        ? "rotate-90 duration-200 ease-in-out"
                        : "duration-300 ease-in-out",
                      "h-5"
                    )}
                  />
                </dt>
                <div
                  className={classNames(
                    (selectedCategory === child.name && idx === openId) ||
                      isFilteringByText
                      ? "max-h-[2400px] overflow-y-auto"
                      : "max-h-0",
                    "overflow-hidden transition-all duration-300"
                  )}>
                  {child?.children.map(grandchild => (
                    <dd
                      key={`${item.id}-${grandchild.id}`}
                      className={classNames(
                        isGrabbing ? "cursor-grabbing" : "cursor-grab",
                        "rounded-md bg-gray-50 pl-2 text-gray-700 duration-200 ease-in-out hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm"
                      )}
                      onMouseDown={handleMouseDown}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp} // In case the mouse leaves the tile while dragging
                      onDragStart={event => {
                        onDragStart(event, grandchild.nodeType, grandchild.id);
                      }}
                      onClick={() => {
                        grandchild.nodeType === NODE_TYPES.ModuleNode
                          ? onClickSidebarItem(WorkflowEditTypes.EditModule, {
                              pipelineId: grandchild.id,
                            })
                          : grandchild.nodeType === NODE_TYPES.ProcessNode
                            ? onClickSidebarItem(
                                WorkflowEditTypes.EditProcess,
                                {
                                  processId: grandchild.id,
                                }
                              )
                            : grandchild.nodeType === NODE_TYPES.SkillNode
                              ? onClickSidebarItem(
                                  WorkflowEditTypes.EditSkill,
                                  {
                                    skillId: grandchild.id,
                                  }
                                )
                              : onClickSidebarItem(
                                  WorkflowEditTypes.EditModel,
                                  {
                                    modelId: grandchild.id,
                                  }
                                );
                      }}
                      draggable>
                      <h3 className="my-2 py-2 text-xs">{grandchild.name}</h3>
                    </dd>
                  ))}
                </div>
              </Fragment>
            ))}
          </div>
        ))}
        {filteredItems.length === 0 && <div>No Results Found</div>}
      </dl>
    </aside>
  );
}
